import { Link } from "wouter";
import { PublicLayout } from "@/components/public/public-layout";
import {
  Container,
  Section,
  SectionIntro,
  PageHero,
  StatGrid,
} from "@/components/public/institutional-ui";
import { firmStats, pillars } from "@/lib/site-content";
import { leadershipTeam, teamIntro } from "@/lib/team-content";
import { TeamAvatar } from "@/components/public/team-avatar";
import { FadeIn } from "@/components/ui/scroll-animations";
import { ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";

export default function AboutPage() {
  return (
    <PublicLayout>
      <PageHero
        eyebrow="About"
        title="Capital built for clarity"
        description="An India-focused private credit and structured finance platform combining institutional discipline with entrepreneurial execution."
      />

      <Section variant="muted" className="!pt-0">
        <Container className="!px-0 sm:!px-0 lg:!px-0 max-w-none">
          <StatGrid stats={firmStats} />
        </Container>
      </Section>

      <Section>
        <Container>
          <div className="grid lg:grid-cols-2 gap-16 lg:gap-24">
            <FadeIn>
              <SectionIntro
                eyebrow="Our foundation"
                title="Built to control outcomes."
                description="Founded by practitioners from global institutions, Godman Capital was created with one conviction: valuable outcomes emerge from superior structuring and alignment—not market timing."
              />
            </FadeIn>
            <FadeIn delay={0.1}>
              <p className="text-muted-foreground leading-relaxed lg:pt-16">
                We operate at the intersection of investment banking discipline and private credit
                innovation—delivering certainty, speed, and confidentiality for founders, CFOs, and
                institutional allocators.
              </p>
            </FadeIn>
          </div>
        </Container>
      </Section>

      <Section variant="muted">
        <Container>
          <SectionIntro eyebrow="Our approach" title="How we work" />
          <div className="grid md:grid-cols-3 gap-px bg-border border border-border">
            {pillars.map((p, i) => (
              <FadeIn key={p.title} delay={i * 0.06}>
                <div className="bg-background p-8 md:p-10 h-full">
                  <h3 className="text-lg font-medium mb-4">{p.title}</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">{p.description}</p>
                </div>
              </FadeIn>
            ))}
          </div>
        </Container>
      </Section>

      <Section>
        <Container>
          <div className="grid md:grid-cols-2 gap-16">
            <FadeIn>
              <p className="gc-eyebrow text-muted-foreground mb-4">Mission</p>
              <h2 className="text-2xl font-medium mb-4">Empowering ambition.</h2>
              <p className="text-muted-foreground leading-relaxed">
                To empower mid-market businesses and sophisticated investors with innovative
                financial solutions that drive sustainable growth and lasting value.
              </p>
            </FadeIn>
            <FadeIn delay={0.1}>
              <p className="gc-eyebrow text-muted-foreground mb-4">Vision</p>
              <h2 className="text-2xl font-medium mb-4">Setting the standard.</h2>
              <p className="text-muted-foreground leading-relaxed italic">
                To be recognized as India&apos;s premier boutique investment banking firm, setting
                new standards in financial advisory excellence.
              </p>
            </FadeIn>
          </div>
        </Container>
      </Section>

      <Section variant="muted">
        <Container>
          <SectionIntro
            eyebrow={teamIntro.eyebrow}
            title={teamIntro.title}
            description={teamIntro.description}
            align="center"
          />
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-14 max-w-6xl mx-auto">
            {leadershipTeam.map((member, i) => (
              <FadeIn key={member.name} delay={i * 0.05}>
                <article className="flex flex-col items-center text-center h-full max-w-sm mx-auto">
                  <TeamAvatar
                    src={member.photo}
                    alt={member.name}
                    className={member.className}
                    imageClassName={member.imageClassName}
                    showFrame={member.showFrame}
                    intrinsic={member.intrinsic}
                  />
                  <h3 className="font-medium text-lg">{member.name}</h3>
                  <p className="text-[10px] uppercase tracking-wider text-muted-foreground mt-2 mb-4 leading-relaxed px-2">
                    {member.role}
                  </p>
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    {member.bio}
                  </p>
                </article>
              </FadeIn>
            ))}
          </div>
        </Container>
      </Section>

      <Section>
        <Container className="text-center max-w-3xl">
          <FadeIn>
            <blockquote className="gc-heading-section text-balance">
              &ldquo;We were built to control outcomes, not just participate in transactions.&rdquo;
            </blockquote>
            <p className="mt-8 gc-eyebrow text-muted-foreground">
              Charan Roy · Co-Founder
            </p>
            <Link href="/contact" className="inline-block mt-10">
              <Button className="gc-btn-primary rounded-none h-12">
                Speak with us
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
          </FadeIn>
        </Container>
      </Section>
    </PublicLayout>
  );
}
